import { useCallback, useEffect, useMemo, useReducer } from 'react';
import moment from 'moment';
import { useQueryClient } from 'react-query';
import { CharterFilingService } from '@wealthlane/api-services/wealthlane-captable-service';
import { confirm } from '@packages/ui/integrations';
import { downloadBase64File, notify } from '@packages/shared/utils';
import { useWealthlaneCapTableApi } from '@/hooks/useWealthlaneCapTableApi';
import useGetCharterFilings from '@/hooks/get/useGetCharterFilings';
import useGetCharterStockClassList from '@/hooks/get/useGetCharterStockClassList';
import usePutStockClassDetails from '@/hooks/put/usePutStockClassDetails';
import { CHARTER_STOCK_CLASSES_CONTEXT_KEY } from '@apps/customer/domains/charter-stock-class/charterStockClassContext';
import { mapEditedRowsToDetailsPayload } from '@apps/customer/domains/charter-stock-class/mapDetailsPayload';
import type { CharterStockClassesTableInfo } from '../store/charterStockClassesTypes';
import {
  charterStockClassesReducer,
  initialCharterStockClassesState,
} from '../store/charterStockClassesReducer';
import type { FilingFilterOption } from '../schema';

export function useCharterStockClassesSession() {
  const [state, dispatch] = useReducer(charterStockClassesReducer, initialCharterStockClassesState);
  const queryClient = useQueryClient();
  const { handleRequest } = useWealthlaneCapTableApi();

  const { data: filings, status: filingsStatus } = useGetCharterFilings(handleRequest);
  const hasCharter = (filings?.length ?? 0) > 0;
  const activeFiling = filings?.find((filing) => filing.isActive);
  const effectiveFilingId = state.tableInfo.charterFilingId ?? activeFiling?.id ?? null;

  const { data: stockClassData, status: stockClassStatus } = useGetCharterStockClassList(
    handleRequest,
    state.tableInfo,
    effectiveFilingId,
  );

  const { mutateAsync: updateDetails, isLoading: isSaveLoading } =
    usePutStockClassDetails(handleRequest);

  useEffect(() => {
    queryClient.setQueryData(CHARTER_STOCK_CLASSES_CONTEXT_KEY, {
      selectedFilingId: state.tableInfo.charterFilingId ?? effectiveFilingId ?? undefined,
      activeFilingId: activeFiling?.id,
    });
  }, [queryClient, state.tableInfo.charterFilingId, effectiveFilingId, activeFiling?.id]);

  const isViewingHistory =
    state.tableInfo.charterFilingId != null &&
    state.tableInfo.charterFilingId !== activeFiling?.id;

  const selectedFiling = useMemo(
    () => filings?.find((filing) => filing.id === state.tableInfo.charterFilingId),
    [filings, state.tableInfo.charterFilingId],
  );

  const filingOptions = useMemo<FilingFilterOption[]>(() => {
    if (!filings) return [];
    return filings.map((filing) => ({
      label: `${filing.filingDate ? moment(filing.filingDate).format('MMM DD, YYYY') : 'Unknown Date'}${filing.isActive ? ' (Active)' : ''}`,
      value: filing.id ?? '',
    }));
  }, [filings]);

  const tableRows = useMemo(
    () =>
      (stockClassData?.items ?? []).map((row) => ({
        ...row,
        dividendType:
          row.dividendType != null && row.dividendType !== ''
            ? Number(row.dividendType)
            : row.dividendType,
        parValue:
          row.parValue != null && row.parValue !== '' ? String(row.parValue) : row.parValue,
        originalIssuePrice:
          row.originalIssuePrice != null && row.originalIssuePrice !== ''
            ? String(row.originalIssuePrice)
            : row.originalIssuePrice,
        dividendRate:
          row.dividendRate != null && row.dividendRate !== ''
            ? String(row.dividendRate)
            : row.dividendRate,
      })),
    [stockClassData?.items],
  );

  const setTableInfo = useCallback((tableInfo: CharterStockClassesTableInfo) => {
    dispatch({ type: 'SET_TABLE_INFO', tableInfo });
  }, []);

  const onFilterChange = useCallback((filters: Record<string, unknown>) => {
    dispatch({
      type: 'PATCH_TABLE_INFO',
      patch: {
        charterFilingId: filters.charterFilingId as string | undefined,
        classType: filters.classType as number | undefined,
        skipCount: 0,
      },
    });
  }, []);

  const onSearchChange = useCallback((filter: string) => {
    dispatch({
      type: 'PATCH_TABLE_INFO',
      patch: {
        filter,
        skipCount: 0,
      },
    });
  }, []);

  const onTablePagingChange = useCallback(
    (info: { skipCount: number; maxResultCount: number; sorting: string }) => {
      dispatch({
        type: 'PATCH_TABLE_INFO',
        patch: info,
      });
    },
    [],
  );

  const showActiveFiling = useCallback(() => {
    dispatch({ type: 'SHOW_ACTIVE_FILING' });
  }, []);

  const handleSave = useCallback(
    async (editedData: unknown[]) => {
      if (!effectiveFilingId) return;
      const payload = mapEditedRowsToDetailsPayload(
        effectiveFilingId,
        editedData as Record<string, unknown>[],
      );
      await updateDetails(payload);
    },
    [effectiveFilingId, updateDetails],
  );

  const handleDocumentDownload = useCallback(
    async (documentDetailId: string) => {
      const result = await handleRequest(
        CharterFilingService.postApiCapTableApiAppCharterFilingDownloadCharterDocument(
          documentDetailId,
        ),
      );
      if (result?.content) {
        downloadBase64File(result.content, result.name ?? 'document');
      }
    },
    [handleRequest],
  );

  const deleteFiling = useCallback(
    async (filingId: string) => {
      const { confirmed } = await confirm({
        title: 'Delete this charter filing?',
        subject: 'this charter filing',
        variant: 'delete',
        preset: true,
        semanticId: 'deleteCharterFilingConfirm',
        onAction: async () => {
          await handleRequest(
            CharterFilingService.deleteApiCapTableApiAppCharterFilingCharterFilings([filingId]),
          );
        },
      });
      if (!confirmed) return;

      queryClient.setQueryData(
        ['getCharterFilings'],
        (previous: typeof filings) => previous?.filter((filing) => filing.id !== filingId),
      );
      dispatch({ type: 'CLEAR_FILING_FILTER' });
      await queryClient.cancelQueries({ queryKey: ['getCharterStockClassList'] });
      await queryClient.invalidateQueries(['getCharterFilings']);
      await queryClient.refetchQueries(['getCharterFilings']);
      await queryClient.invalidateQueries(['getCharterStockClassList']);
      notify.success('Charter filing deleted successfully.');
    },
    [handleRequest, queryClient],
  );

  const tableFilters = useMemo(
    () => ({
      charterFilingId: state.tableInfo.charterFilingId,
      classType: state.tableInfo.classType,
    }),
    [state.tableInfo.charterFilingId, state.tableInfo.classType],
  );

  const loading =
    filingsStatus === 'loading' && filings == null
      ? 'loading'
      : stockClassStatus === 'loading'
        ? 'loading'
        : stockClassStatus === 'error'
          ? 'error'
          : 'success';

  return {
    filings,
    hasCharter,
    activeFiling,
    selectedFiling,
    isViewingHistory,
    filingOptions,
    tableRows,
    tableInfo: state.tableInfo,
    tableFilters,
    totalCount: stockClassData?.totalCount ?? 0,
    loading,
    isSaveLoading,
    setTableInfo,
    onFilterChange,
    onSearchChange,
    onTablePagingChange,
    showActiveFiling,
    handleSave,
    handleDocumentDownload,
    deleteFiling,
  };
}
